import { useEffect, type ReactNode } from 'react';
import { BRAND } from '@platform/shared';
import { useAddToHomeScreen } from '../components/mobile/InstallSheet';
import { Icon } from '../components/ui';
import { Link, navigate } from '../lib/router';
import { paintChrome } from '../lib/theme';
import { useSession } from '../stores/session';

const FEATURES: { icon: ReactNode; text: string }[] = [
  {
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9s1.3-6.4 3.8-9z" />
      </>
    ),
    text: 'Crypto, commodities & stocks',
  },
  { icon: <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />, text: 'One USDT balance for every market' },
  { icon: <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />, text: 'Live prices, instant execution' },
  { icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />, text: 'Stop-loss & take-profit built in' },
];

/** Landing screen for signed-out visitors on phones (dark, with a diagonal light streak). Always dark, whatever the theme. */
export function WelcomePage() {
  const s = useSession();
  const install = useAddToHomeScreen();
  useEffect(() => {
    document.title = BRAND.name;
    paintChrome('#000000');
    return () => paintChrome();
  }, []);
  useEffect(() => {
    if (s.ready && s.me) navigate('/portfolio', { replace: true });
  }, [s.ready, s.me]);

  return (
    <div className="relative flex min-h-full flex-col overflow-hidden bg-black text-white">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div
          className="absolute inset-x-0 top-0 h-[600px]"
          style={{
            background:
              'radial-gradient(34% 20% at 100% 34%, rgba(130,160,255,0.55) 0%, rgba(130,160,255,0) 100%), radial-gradient(120% 72% at 15% 0%, rgba(52,84,240,0.95) 0%, rgba(28,52,190,0.55) 32%, rgba(8,14,60,0.35) 60%, rgba(0,0,0,0) 82%)',
          }}
        />
        <div
          className="absolute top-[-1400px] left-[44%] h-[1400px] w-[2400px] origin-bottom-left rotate-[41deg]"
          style={{ background: 'linear-gradient(0deg, rgba(70,105,255,0.45) 0%, rgba(6,10,34,0.92) 4%, #000 40%)' }}
        />
        <div
          className="absolute top-[-1px] left-[44%] h-0.5 w-[1600px] origin-left rotate-[41deg]"
          style={{
            background: 'linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.95) 6%, rgba(150,175,255,0.8) 22%, rgba(120,150,255,0) 40%)',
            boxShadow: '0 0 22px 5px rgba(90,125,255,0.6)',
          }}
        />
        <div className="absolute inset-x-0 bottom-0 h-[62%]" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, #000 42%)' }} />
      </div>

      <div className="relative mx-auto flex w-full max-w-[440px] flex-1 flex-col px-8 pt-[max(40px,env(safe-area-inset-top))] pb-[max(28px,env(safe-area-inset-bottom))]">
        <span className="text-[23px] font-semibold tracking-[-0.02em]">{BRAND.name}</span>

        <h1 className="m-0 mt-[clamp(56px,17vh,140px)] font-serif text-[46px] leading-[1.02] font-normal tracking-[-0.01em]">
          Trade every market from one account
        </h1>

        <ul className="m-0 mt-9 flex list-none flex-col gap-2.5 p-0">
          {FEATURES.map((f) => (
            <li key={f.text} className="flex items-center gap-3.5 text-[16px] text-white/90">
              <span aria-hidden="true" className="grid size-8 shrink-0 place-items-center rounded-[7px] bg-white/14">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  {f.icon}
                </svg>
              </span>
              {f.text}
            </li>
          ))}
        </ul>

        <div className="mt-auto flex flex-col gap-3 pt-12">
          <Link to="/register" className="flex h-[52px] items-center justify-center rounded-full bg-white text-[17px] font-semibold text-black no-underline">
            Create account
          </Link>
          <Link to="/login" className="flex h-[52px] items-center justify-center rounded-full border border-white/22 text-[17px] font-semibold text-white no-underline">
            Log in
          </Link>
          {!install.installed && (
            <button
              type="button"
              onClick={() => void install.start()}
              className="flex h-[52px] items-center justify-center gap-2.5 rounded-full border border-white/22 text-[17px] font-semibold text-white"
            >
              <Icon name="addSquare" size={20} />
              Add to Home Screen
            </button>
          )}
        </div>
        <p className="m-0 mt-[14px] text-center text-[12px] leading-[1.45] text-white/62">
          CFDs are leveraged products and carry a high risk of losing money rapidly.
        </p>
      </div>
      {install.sheet}
    </div>
  );
}
